import { useRef, useState } from 'react';
import { ListMusic, LoaderCircle, Music2, Plus, Search, Upload } from 'lucide-react';
import type { TrackRole } from '../../types/audio';

export interface ImportSongRequest {
  title: string;
  artist: string;
  bpm: string;
  key: string;
  tracks: Array<{ file: File; name: string; role: TrackRole }>;
}

interface LibraryPanelProps {
  search: string;
  onSearch: (value: string) => void;
  song: { title: string; artist: string; trackCount: number } | null;
  isImporting: boolean;
  onImport: (request: ImportSongRequest) => Promise<void>;
}

export function LibraryPanel({ search, onSearch, song, isImporting, onImport }: LibraryPanelProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  return (
    <aside className="border-b border-white/8 bg-[#0b0f15] p-4 lg:border-r lg:border-b-0">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <p className="eyebrow">Sesión actual</p>
          <h1 className="mt-1 text-lg font-semibold">Biblioteca</h1>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={() => setIsDialogOpen(true)}
          aria-label="Importar canción"
        >
          <Plus size={18} />
        </button>
      </div>

      <label className="search-field">
        <Search size={16} />
        <input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder="Buscar pistas"
          aria-label="Buscar pistas"
        />
      </label>

      <nav className="mt-5 space-y-1" aria-label="Secciones de la biblioteca">
        <button className="nav-item nav-item-active" type="button">
          <Music2 size={17} />
          Canción cargada
          <span>{song ? 1 : 0}</span>
        </button>
        <button className="nav-item" type="button" disabled>
          <ListMusic size={17} />
          Repertorios
          <span>Próximamente</span>
        </button>
      </nav>

      {song ? (
        <article className="mt-6 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3">
          <p className="truncate text-sm font-semibold text-slate-100">{song.title}</p>
          <p className="mt-1 truncate text-xs text-slate-500">
            {song.artist || 'Artista no indicado'}
          </p>
          <p className="mt-3 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
            {song.trackCount} pistas en memoria
          </p>
        </article>
      ) : (
        <div className="mt-10 rounded-xl border border-dashed border-white/10 px-5 py-8 text-center">
          <Music2 className="mx-auto text-slate-600" size={24} />
          <p className="mt-3 text-sm font-medium text-slate-300">Importa una canción</p>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Selecciona los WAV de una misma línea de tiempo.
          </p>
          <button
            className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-cyan-300"
            type="button"
            onClick={() => setIsDialogOpen(true)}
          >
            <Upload size={14} /> Importar archivos WAV
          </button>
        </div>
      )}

      {isDialogOpen && (
        <ImportDialog
          isLoading={isImporting}
          onClose={() => !isImporting && setIsDialogOpen(false)}
          onImport={async (request) => {
            await onImport(request);
            setIsDialogOpen(false);
          }}
        />
      )}
    </aside>
  );
}

function ImportDialog({
  isLoading,
  onClose,
  onImport,
}: {
  isLoading: boolean;
  onClose: () => void;
  onImport: (request: ImportSongRequest) => Promise<void>;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [bpm, setBpm] = useState('');
  const [key, setKey] = useState('');
  const [tracks, setTracks] = useState<ImportSongRequest['tracks']>([]);
  const [error, setError] = useState<string | null>(null);

  function addFiles(files: FileList | null) {
    const candidates = Array.from(files ?? []);
    const invalid = candidates.find((file) => !file.name.toLowerCase().endsWith('.wav'));
    if (invalid) {
      setError(`“${invalid.name}” no es WAV. Importa únicamente archivos .wav.`);
      return;
    }
    setError(null);
    setTracks(
      candidates.map((file) => ({
        file,
        name: file.name.replace(/\.wav$/i, ''),
        role: inferRole(file.name),
      })),
    );
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        className="import-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-title"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">Nueva canción</p>
            <h2 id="import-title" className="mt-1 text-xl font-semibold">
              Importar multisecuencias
            </h2>
          </div>
          <button
            className="text-sm text-slate-500 hover:text-white"
            type="button"
            onClick={onClose}
            disabled={isLoading}
          >
            Cerrar
          </button>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <Field label="Título">
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Mi canción"
            />
          </Field>
          <Field label="Artista">
            <input
              value={artist}
              onChange={(event) => setArtist(event.target.value)}
              placeholder="Opcional"
            />
          </Field>
          <Field label="BPM">
            <input
              value={bpm}
              onChange={(event) => setBpm(event.target.value)}
              inputMode="numeric"
              placeholder="Opcional"
            />
          </Field>
          <Field label="Tonalidad">
            <input
              value={key}
              onChange={(event) => setKey(event.target.value)}
              placeholder="Opcional"
            />
          </Field>
        </div>
        <input
          ref={fileInputRef}
          className="sr-only"
          type="file"
          accept=".wav,audio/wav,audio/x-wav"
          multiple
          onChange={(event) => addFiles(event.target.files)}
        />
        <button
          className="drop-target mt-5"
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
        >
          <Upload size={18} />
          <span>
            <strong>Seleccionar archivos WAV</strong>
            <small>Todos deben comenzar en el mismo punto de tiempo.</small>
          </span>
        </button>
        {error && <p className="form-error">{error}</p>}
        {tracks.length > 0 && (
          <div className="mt-4 max-h-44 space-y-2 overflow-auto pr-1">
            {tracks.map((track, index) => (
              <div key={`${track.file.name}-${index}`} className="import-track-row">
                <span className="truncate">{track.name}</span>
                <select
                  value={track.role}
                  onChange={(event) =>
                    setTracks((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, role: event.target.value as TrackRole }
                          : item,
                      ),
                    )
                  }
                >
                  <option value="music">Música</option>
                  <option value="click">Clic</option>
                  <option value="guide">Guía</option>
                </select>
              </div>
            ))}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button className="dialog-button" type="button" onClick={onClose} disabled={isLoading}>
            Cancelar
          </button>
          <button
            className="dialog-button dialog-button-primary"
            type="button"
            disabled={tracks.length === 0 || isLoading}
            onClick={() => void onImport({ title, artist, bpm, key, tracks })}
          >
            {isLoading ? <LoaderCircle size={15} className="animate-spin" /> : <Upload size={15} />}{' '}
            {isLoading
              ? 'Decodificando…'
              : `Cargar ${tracks.length || ''} pista${tracks.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="dialog-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function inferRole(name: string): TrackRole {
  const normalised = name.toLowerCase();
  if (/(click|clic|metronom)/.test(normalised)) return 'click';
  if (/(guide|gu[ií]a|cue)/.test(normalised)) return 'guide';
  return 'music';
}
